import { Layout } from 'antd';
import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import GithubLinks from './GithubLinks/GithubLinks';
import Loader from '../../../components/ui/Loader/Loader';
import { BuildDeployActions } from '../../../libs/store/actions/BuildDeployActions';
import { DashboardActions } from '../../../libs/store/actions/DashboardActions';
import { IApplicationState, useAppSelector } from '../../../libs/store/reducers';
import AppHeader from './AppHeader/AppHeader';
import ProductTour from './ProductTour/ProductTour';
import Sidebar from './Sidebar/Sidebar';
import { SideBarKey } from './Sidebar/SidebarConstants';
import { ICountLandingZone, routesWithLoadingDisabled } from './HomePageConstants';
import { calculateLandingZones } from './HomePageHelperFunction';
import { ViewModeProvider } from '../../../contexts/ViewModeContext';
import './HomePageStyles.less';
import RouteGuard from './RouteGuard';

const { Content } = Layout;

interface IHomePageProps {
  children: ReactNode;
}

const HomePage = ({ children }: IHomePageProps) => {
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  const history = useHistory();
  const dispatch = useDispatch();

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // TODO: Set-up homepage state in redux to keep track of current page when more pages are added.

  const {
    isExpanded,
    hiddenSidebar,
    activeKey,
    loginLoading,
    userLoading,
    dashboardLoading,
    jobRunLoading,
    selectedProjectId,
    overViewLoading,
    buildDeployLoading,
    landingZones,
    appsAndSolutionsLoading,
  } = useAppSelector(
    ({
      sidebar: { isExpanded, hiddenSidebar, activeKey },
      overview: { selectedProjectId },
      login,
      user,
      dashboard,
      operations,
      overview,
      buildDeploy,
      appsAndSolutions,
    }: IApplicationState) => ({
      landingZones: buildDeploy.landingZones,
      isExpanded,
      hiddenSidebar,
      activeKey,
      loginLoading: login.loading,
      userLoading: user.loading,
      dashboardLoading: dashboard.loading,
      jobRunLoading: operations.loadingJobRun,
      overViewLoading: overview.loading,
      selectedProjectId,
      buildDeployLoading: buildDeploy.loading || buildDeploy.loadingCount !== 0,
      appsAndSolutionsLoading: appsAndSolutions.loading || appsAndSolutions.loadingCount !== 0,
    }),
  );

  const dispatcher = {
    getBuildDeployDetails: (data: string) =>
      dispatch(BuildDeployActions.getBuildDeployDetailStarted(data)),
    setTotalEnvironments: (data: ICountLandingZone) =>
      dispatch(BuildDeployActions.setTotalEnvironmentsCount(data)),
    getAssessmentsState: (data: string) => dispatch(DashboardActions.getAssessmentDataStart(data)),
  };

  const getBuildDeployDetails = (projectId: string) => dispatcher.getBuildDeployDetails(projectId);

  useEffect(() => {
    selectedProjectId && getBuildDeployDetails(selectedProjectId);
    selectedProjectId && dispatcher.getAssessmentsState(selectedProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  useEffect(() => {
    const count = calculateLandingZones(landingZones);

    dispatcher.setTotalEnvironments(count);
  }, [landingZones]);

  const loadingDisabled = routesWithLoadingDisabled.includes(history.location.pathname);

  return (
    <>
      <Loader
        loading={
          loadingDisabled
            ? false
            : // Only block the entire portal on auth-critical bootstrap
              // (login + user permissions). Data fetches like dashboard /
              // build-deploy / overview / apps-and-solutions used to be in
              // this OR-chain, which made a single slow API freeze the
              // whole UI behind a full-page spinner. They now render with
              // skeletons inside their own cards so the shell paints
              // immediately and slow APIs fill in asynchronously.
              loginLoading || userLoading
        }
        loaderName=""
      >
        <Layout>
          <Sidebar />

          <Layout className="main-content-container">
            <ViewModeProvider>
              <AppHeader isScrolled={scrollPosition > 15} />
              <Content>
                <RouteGuard>{children}</RouteGuard>
              </Content>
            </ViewModeProvider>
          </Layout>
        </Layout>
      </Loader>
      {/*TODO: Need to change it to label rather than key */}
      <ProductTour startTour={activeKey === SideBarKey.TOUR_GUIDE} />
      <GithubLinks visible={activeKey === SideBarKey.GITHUB_LINKS} />
    </>
  );
};

export default HomePage;
